import React from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

export function BackgroundSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
    >
      <Box
        sx={{
          background: "linear-gradient(180deg, #162667 0%, #1B2E85 100%)",
          py: { xs: 6, md: 10 },
          mt: { xs: 6, md: 8 },
          color: "white",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 10 },
          }}
        >
          {/* --- Text Section --- */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={2}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 2, opacity: 0.8 }}
              >
                Hereditary Cancer Clinic (HCC)
              </Typography>

              <Typography
                variant="h3"
                fontWeight="bold"
                sx={{
                  lineHeight: 1.2,
                  textShadow: "0 1px 0 rgba(0,0,0,0.2)",
                }}
              >
                Background
              </Typography>

              <Divider
                sx={{
                  width: 64,
                  borderColor: "rgba(255,255,255,0.4)",
                  my: 1,
                }}
              />

              <Typography
                variant="body1"
                sx={{
                  maxWidth: "58ch",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.8,
                  opacity: 0.95,
                }}
              >
                The Medical University of South Carolina (MUSC) created the
                Hereditary Cancer Clinic (HCC) to better support patients with
                genetic variants linked to cancers like BRCA-related breast and
                ovarian cancer or Lynch syndrome. Using a five-step
                implementation mapping approach, the team identified barriers,
                streamlined referrals, and improved education and follow-up for
                high-risk patients. The clinic now offers a coordinated,
                guideline-driven system that connects genetics experts,
                specialists, and patients—serving as a model for improving
                hereditary cancer management and long-term care.
              </Typography>

              <Button
                component="a"
                href="/AllenHughesPaper.pdf"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "999px",
                  border: "1.5px solid rgba(255,255,255,0.4)",
                  background: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(6px)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "rgba(255,255,255,0.15)",
                    borderColor: "rgba(255,255,255,0.7)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Read the Paper (PDF)
              </Button>
            </Stack>
          </Box>

          {/* --- Image Section --- */}
          <Box
            component="a"
            href="/AllenHughesPaper.pdf"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              flex: 1,
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
              cursor: "pointer",
              outline: "1px solid rgba(255,255,255,0.15)",
              transition: "transform 0.25s ease, box-shadow 0.25s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
              },
            }}
          >
            <Box
              component="img"
              src="./background_img_3.png"
              alt="Hereditary Cancer Clinic background"
              loading="lazy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />

            <Box
              className="overlay"
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.3s ease-in-out",
                fontWeight: 700,
                fontSize: "1.05rem",
                textAlign: "center",
                px: 3,
                "&:hover": { opacity: 1 },
              }}
            >
              Click to open the full paper
            </Box>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
}

export function LearningSystemSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
    >
      <Box
        sx={{
          background: "linear-gradient(to right, #f5f7fb 60%, #ffffff)",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {/* Left Text Area */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                px: 2,
              }}
            >
              {/* Title */}
              <Typography
                variant="h6"
                fontWeight="bold"
                color="#1B2E85"
                sx={{ mb: 1 }}
              >
                Toward a Smarter Cancer Care Model
              </Typography>

              {/* Subtitle */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Connecting clinical guidelines to real-world results through
                intelligent systems.
              </Typography>

              {/* Quote with vertical line */}
              <Box sx={{ display: "flex", alignItems: "stretch" }}>
                <Box
                  sx={{
                    width: "15px",
                    bgcolor: "#1B2E85",
                    mr: 2,
                    borderRadius: 1,
                  }}
                />
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color="#1B2E85"
                  sx={{ lineHeight: 1.4 }}
                >
                  We believe the Hereditary Cancer Clinic will help create a
                  learning healthcare system for cancer genetics.
                </Typography>
              </Box>

              {/* OPTIONAL Button
              <Button
                variant="outlined"
                sx={{
                  mt: 4,
                  borderColor: "#1B2E85",
                  color: "#1B2E85",
                  "&:hover": {
                    backgroundColor: "#1B2E85",
                    color: "white",
                  },
                }}
              >
                See Our Approach
              </Button> */}
            </Box>

            {/* Image */}

            <PhotoProvider>
              <PhotoView src="/learninghealthsystem.png">
                <Box
                  component="img"
                  src="/learninghealthsystem.png"
                  alt="Learning Health System"
                  sx={{
                    cursor: "pointer",
                    flexShrink: 0,
                    width: "100%",
                    maxWidth: 450,
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
}

export function AiEnhancedManagementSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
    >
      <Box
        sx={{
          bgcolor: "#ffffff",
          py: { xs: 8, md: 12 },
          mb: 0,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          {/* Section Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            color="#1B2E85"
            sx={{ mb: 4 }}
          >
            AI Enhanced Management Strategies
          </Typography>

          {/* Gene-Cancer Map Image */}
          <PhotoProvider>
            <PhotoView src="/aimanagementstrategies.png">
              <Box
                component="img"
                src="/aimanagementstrategies.png"
                alt="AI Management Strategies"
                sx={{
                  cursor: "pointer",
                  width: "100%",
                  maxWidth: 900,
                  mx: "auto",
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              />
            </PhotoView>
          </PhotoProvider>

          {/* TO DO: Subheading under gene image */}
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mt: 2 }}
          ></Typography>
        </Container>
      </Box>
    </motion.div>
  );
}

export function AiCognitiveLimitSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
    >
      <Box
        sx={{
          bgcolor: "#f9fafc",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {/* Text on the Left */}
            <Box sx={{ flex: 1, px: 2 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                color="#1B2E85"
                sx={{ mb: 2 }}
              >
                The Role of AI in Managing Complexity
              </Typography>
              <Typography variant="body1" color="text.secondary">
                As we deal with more and more cancer susceptibility genes,
                management becomes more and more complex. The human mind is
                approaching its limit and we will increasingly need to depend on
                AI to keep ahead.
              </Typography>
            </Box>

            {/* Image on the Right */}
            <PhotoProvider>
              <PhotoView src="chart.png">
                <Box
                  component="img"
                  src="/chart.png"
                  alt="BRCA1 Chart"
                  sx={{
                    cursor: "pointer",
                    width: "100%",
                    maxWidth: 650,
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
}

export function GuidelineFlowSection() {
  return (
    <Box sx={{ bgcolor: "#ffffff", py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <PhotoProvider>
          {/* Block 1 */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              color="#1B2E85"
              textAlign="center"
            >
              Management strategies for individuals with hereditary cancer gene
              mutations are spread across nine separate NCCN guidelines.
            </Typography>

            <Box
              sx={{
                width: "60px",
                height: "3px",
                backgroundColor: "#1B2E85",
                mx: "auto",
                my: 2,
                borderRadius: 2,
              }}
            />

            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: 2,
                my: 4,
                mx: "auto",
                maxWidth: 900,
              }}
            >
              <PhotoView src="/nccn-disbursed.png">
                <Box
                  component="img"
                  src="/nccn-disbursed.png"
                  alt="Disbursed NCCN Guidelines"
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </Paper>
          </motion.div>

          {/* Block 2 */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h6"
              fontWeight="medium"
              color="text.primary"
              textAlign="center"
            >
              The guidelines change frequently and become more complex.
            </Typography>

            <Box
              sx={{
                width: "60px",
                height: "3px",
                backgroundColor: "#1B2E85",
                mx: "auto",
                my: 2,
                borderRadius: 2,
              }}
            />

            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: 2,
                my: 4,
                mx: "auto",
                maxWidth: 900,
              }}
            >
              <PhotoView src="/nccn-complexity.png">
                <Box
                  component="img"
                  src="/nccn-complexity.png"
                  alt="Changing Guidelines Complexity"
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </Paper>
          </motion.div>
        </PhotoProvider>
      </Container>

      {/* Final Summary */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <Box
          sx={{
            backgroundColor: "#1B2E85",
            color: "white",
            py: { xs: 8, md: 10 },
            px: 2,
            mt: 11,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              maxWidth: 1000,
              width: "100%",
              px: { xs: 2, sm: 4 },
            }}
          >
            {/* Vertical Line */}
            <Box
              sx={{
                width: "15px",
                backgroundColor: "white",
                borderRadius: 1,
                minHeight: "100%",
                mr: 3,
              }}
            />

            {/* Text */}
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="left"
              sx={{ lineHeight: 1.4 }}
            >
              We are developing a Clinical Decision Support tool that will
              simplify managing carriers. The prototype has been used in our
              Hereditary Cancer Clinic on over 700 patients.
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}

export const FinalCallToAction = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      viewport={{ once: true }}
    >
      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          py: { xs: 8, md: 10 },
          px: 2,
          width: "100%",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h2"
          fontWeight="bold"
          color="#1B2E85"
          sx={{ mb: 2 }}
        >
          Interested in learning more?
        </Typography>

        {/* Centered Divider */}
        <Box
          sx={{
            height: "2px",
            width: "50px",
            backgroundColor: "#1B2E85",
            margin: "0 auto",
            mb: 3,
          }}
        />

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
        >
          Learn more about hereditary cancer care and genetic counseling
          services at MUSC.
        </Typography>

        <Stack justifyContent="center" alignItems="center">
          <Button
            href="https://hollingscancercenter.musc.edu/patient-care/genetic-counseling-and-hereditary-cancer/hereditary-cancer-clinic"
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            sx={{
              color: "#1B2E85",
              borderColor: "#1B2E85",
              fontWeight: "bold",
              px: 4,
              py: 1.2,
              textTransform: "none",
              borderRadius: 999,
              "&:hover": {
                backgroundColor: "#1B2E85",
                color: "white",
              },
            }}
          >
            MUSC Hereditary Cancer Clinic
          </Button>
        </Stack>
      </Box>
    </motion.div>
  );
};
